/**
 * Document Event Protocol v1 — SDK types + DocumentEventStream
 *
 * Canonical source for OkraEvent / DocumentEvent types.
 * Server mirror: apps/api/server/src/da/event-protocol.ts (keep in sync).
 */

import { Cause, Data, Effect, Exit, Pull, Queue, Scope } from 'effect';

const eventStreamCauses = new WeakMap<DocumentEventStreamError, unknown>();

export class DocumentEventStreamError extends Data.TaggedError('DocumentEventStreamError')<{
  readonly method: 'connect' | 'dispatch' | 'buffer_overflow';
  readonly status?: number;
}> {
  constructor(input: {
    method: 'connect' | 'dispatch' | 'buffer_overflow';
    status?: number;
    cause: unknown;
  }) {
    super({
      method: input.method,
      ...(input.status === undefined ? {} : { status: input.status }),
    });
    eventStreamCauses.set(this, input.cause);
  }
  get cause(): unknown {
    return eventStreamCauses.get(this);
  }
}

// ── Cross-stack base event type ─────────────────────────────────────────────

export interface OkraEvent {
  v: number;
  type: string;
  timestamp: number;
  data: Record<string, unknown>;
}

// ── Document-specific events ────────────────────────────────────────────────

export interface DocumentEvent extends OkraEvent {
  v: 1;
  type: `document.${string}`;
  documentId: string;
}

export type DocumentEventType =
  // Tier 1 — Progress
  | 'document.phase_changed'
  | 'document.vendor_started'
  | 'document.vendor_completed'
  | 'document.vendor_failed'
  | 'document.parse_complete'
  | 'document.hydrated'
  | 'document.verification_progress'
  | 'document.ready'
  | 'document.error'
  | 'document.thumbnail_ready'
  // Tier 2 — Granular
  | 'document.node_verified'
  | 'document.page_resolved'
  | 'document.extraction_started';

// ── DocumentEventStream ─────────────────────────────────────────────────────

export type DocumentEventHandler = (event: DocumentEvent) => void;

export interface DocumentEventStreamOptions {
  /** Filter to specific event types. Omit or null = all public events. */
  events?: DocumentEventType[];
  signal?: AbortSignal;
  /** Maximum unread iterator events, default 1024. Overflow closes and rejects the iterator. */
  bufferSize?: number;
}

/**
 * Lightweight WebSocket event stream for document lifecycle events.
 *
 * Usage:
 *   const stream = new DocumentEventStream(wsUrl);
 *   stream.on('document.ready', (evt) => console.log('Done!', evt.data));
 *   stream.connect();
 *   // or: for await (const evt of stream) { ... }
 */
export class DocumentEventStream {
  #ws: WebSocket | null = null;
  #url: string;
  #options: DocumentEventStreamOptions;
  #scope = Scope.makeUnsafe();
  #handlers = new Map<string, Set<DocumentEventHandler>>();
  #closed = false;
  #discarded = false;
  #failure: DocumentEventStreamError | undefined;
  #queue: Queue.Queue<DocumentEvent, Cause.Done | DocumentEventStreamError> | undefined;
  #iterator: AsyncGenerator<DocumentEvent> | undefined;
  #iteratorEnded = false;

  constructor(url: string, options?: DocumentEventStreamOptions) {
    this.#url = url;
    this.#options = options ?? {};
    const capacity = this.#options.bufferSize ?? 1024;
    if (!Number.isSafeInteger(capacity) || capacity < 1)
      throw new RangeError('Document event bufferSize must be a positive safe integer');
    const signal = this.#options.signal;
    if (signal) {
      const onAbort = () => this.close();
      Effect.runSync(
        Effect.acquireRelease(
          Effect.sync(() => signal.addEventListener('abort', onAbort, { once: true })),
          () => Effect.sync(() => signal.removeEventListener('abort', onAbort)),
        ).pipe(Effect.provideService(Scope.Scope, this.#scope)),
      );
      if (signal.aborted) this.close();
    }
  }

  #finish = Effect.fn('sdk.documentEvents.finish')(function* (
    this: DocumentEventStream,
    drain: boolean,
    failure?: DocumentEventStreamError,
  ) {
    this.#closed = true;
    if (!drain) this.#discarded = true;
    if (failure) this.#failure = failure;
    this.#handlers.clear();
    yield* Scope.close(this.#scope, Exit.void);
    if (this.#queue) {
      if (failure) yield* Queue.fail(this.#queue, failure);
      else yield* Queue.end(this.#queue);
      if (!drain) yield* Queue.shutdown(this.#queue);
    }
  });

  #connectEffect = Effect.fn('sdk.documentEvents.connect')(function* (this: DocumentEventStream) {
    if (this.#closed || this.#ws) return;
    const ws = yield* Effect.acquireRelease(
      Effect.try({
        try: () => new WebSocket(this.#url),
        catch: (cause) => new DocumentEventStreamError({ method: 'connect', cause }),
      }),
      (socket) =>
        Effect.sync(() => {
          socket.onmessage = null;
          socket.onerror = null;
          socket.onclose = null;
          if (this.#ws === socket) this.#ws = null;
          try {
            socket.close();
          } catch {
            /* best-effort socket teardown */
          }
        }),
    );
    if (this.#closed) return;
    this.#ws = ws;
    ws.onmessage = (event) => {
      if (this.#closed || this.#ws !== ws) return;
      this.#runSync(this.#dispatch(event));
    };
    ws.onerror = () => {
      /* the WebSocket close event ends the subscription */
    };
    ws.onclose = () => {
      if (this.#ws === ws) Effect.runSync(this.#finish(true));
    };
  });

  #dispatch = Effect.fn('sdk.documentEvents.dispatch')(function* (
    this: DocumentEventStream,
    message: MessageEvent,
  ) {
    const raw = typeof message.data === 'string' ? message.data : '';
    if (raw.startsWith('cf_agent_state:')) return;
    let event: DocumentEvent;
    try {
      event = JSON.parse(raw);
    } catch {
      return;
    }
    if (
      !event ||
      event.v !== 1 ||
      typeof event.type !== 'string' ||
      !event.type.startsWith('document.')
    )
      return;
    if (this.#options.events && !this.#options.events.includes(event.type as DocumentEventType))
      return;
    const handlers = [
      ...(this.#handlers.get(event.type) ?? []),
      ...(this.#handlers.get('*') ?? []),
    ];
    for (const handler of handlers)
      yield* Effect.try({
        try: () => handler(event),
        catch: (cause) => new DocumentEventStreamError({ method: 'dispatch', cause }),
      });
    if (this.#closed || !this.#queue) return;
    const accepted = yield* Queue.offer(this.#queue, event);
    if (!accepted)
      yield* this.#finish(
        false,
        new DocumentEventStreamError({
          method: 'buffer_overflow',
          cause: new Error('Document event iterator buffer exceeded its capacity'),
        }),
      );
  });

  #runSync(program: Effect.Effect<void, DocumentEventStreamError, Scope.Scope>): void {
    Effect.runSync(
      program.pipe(
        Effect.provideService(Scope.Scope, this.#scope),
        Effect.catch((error) =>
          this.#finish(false, error).pipe(Effect.andThen(Effect.fail(error.cause))),
        ),
      ),
    );
  }

  /** Open once. Repeated calls do not replace the owned socket. */
  connect(): void {
    this.#runSync(this.#connectEffect());
  }

  /** Subscribe to live callbacks without retaining an iterator backlog. */
  on(type: DocumentEventType | '*', handler: DocumentEventHandler): () => void {
    if (this.#closed) return () => {};
    let set = this.#handlers.get(type);
    if (!set) {
      set = new Set();
      this.#handlers.set(type, set);
    }
    set.add(handler);
    return () => {
      set.delete(handler);
      if (set.size === 0) this.#handlers.delete(type);
    };
  }

  /** Close the socket, detach listeners and discard unread iterator events. */
  close(): void {
    Effect.runSync(this.#finish(false));
  }

  get connected(): boolean {
    return this.#ws?.readyState === WebSocket.OPEN;
  }

  #next = Effect.fn('sdk.documentEvents.next')(function* (
    this: DocumentEventStream,
  ): Effect.fn.Return<IteratorResult<DocumentEvent>, DocumentEventStreamError> {
    const done: IteratorReturnResult<undefined> = { done: true, value: undefined };
    if (this.#iteratorEnded) return done;
    const result = yield* Effect.exit(
      Queue.take(this.#queue!).pipe(
        Effect.map((value): IteratorResult<DocumentEvent> => ({ done: false, value })),
        Pull.catchDone(() => Effect.succeed(done)),
      ),
    );
    if (this.#iteratorEnded) return done;
    if (this.#failure) {
      this.#iteratorEnded = true;
      return yield* Effect.fail(this.#failure);
    }
    if (this.#discarded) {
      this.#iteratorEnded = true;
      return done;
    }
    if (Exit.isFailure(result)) {
      this.#iteratorEnded = true;
      yield* this.#finish(false);
      return yield* Effect.failCause(result.cause);
    }
    if (result.value.done) this.#iteratorEnded = true;
    return result.value;
  });

  /**
   * Subscribe at iterator creation; create it before connect() to receive initial frames.
   * Callbacks alone retain no backlog. One shared iterator buffers at most bufferSize
   * events and fails explicitly on overflow. Remote close drains accepted events;
   * close(), abort and iterator return discard them and release pending pulls.
   */
  [Symbol.asyncIterator](): AsyncGenerator<DocumentEvent> {
    if (this.#iterator) return this.#iterator;
    this.#queue = Effect.runSync(
      Queue.dropping<DocumentEvent, Cause.Done | DocumentEventStreamError>(
        this.#options.bufferSize ?? 1024,
      ),
    );
    if (this.#closed) Effect.runSync(this.#finish(false, this.#failure));
    const owner = this;
    this.#iterator = {
      [Symbol.asyncIterator]() {
        return this;
      },
      next: () =>
        Effect.runPromise(
          owner.#next().pipe(
            Effect.onInterrupt(() => owner.#finish(false)),
            Effect.mapError((error) => error.cause),
          ),
        ),
      return: (value) =>
        Effect.runPromise(owner.#finish(false).pipe(Effect.as({ done: true as const, value }))),
      throw: (error) =>
        Effect.runPromise(owner.#finish(false).pipe(Effect.andThen(Effect.fail(error)))),
    };
    return this.#iterator;
  }
}
